import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  line, Color, LIST_PAGES, PAY_RETURN_GATEWAY_PARAM, PAY_RETURN_SESSION_PARAM, draftDocumentTitle, NEW_DOCUMENT_ID, Size, taxJurisdictionRowLead, documentPageMode, invoicePills, pageKindLineText, t, type InvoiceAnswer,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { WriteOffDateField } from './WriteOffDateField';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { HelpButton } from '../../components/modals/HelpModal';
import { Modal } from '../../components/modals/Modal';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { BannerPills } from './BannerPills';
import { ApprovalReason } from './ApprovalReason';
import { DocumentViewPage, listPricedCell, type DocumentLinesDescriptor } from './DocumentViewPage';
import { InvoiceForm } from './InvoiceForm';
import { INVOICES_PATH, post, useApi } from './api';
import { documentPdfPath } from './ViewPdfLink';
import { DocumentFilesPanel } from './DocumentFilesPanel';
import { SourceLinkPanel } from './SourceLinkPanel';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { DealTeamPanel } from './DealTeamPanel';
import { StaleSendBanner, staleSendFor } from './StaleSendWarning';
import { DocumentSendWindow, type DocumentSendFields } from './DocumentSendWindow';
import { DeliveredPanel } from './DeliveredPanel';
import { useDocumentToday, useViewerZone } from '../../shell/ViewerZone';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import type { TileRow } from './tileItems';
import { AccountJournalTile } from '../accounting/AccountJournalTile';
import { PayInvoiceModal } from './PayInvoiceModal';
import { PayReturnNotice } from './PayReturnNotice';

/** The displayed status as one pill, with Draft (no number yet) read off the number rather than invented here. */
type OpenModal = 'send' | 'issue' | 'dispute' | 'writeOff' | 'undoWriteOff' | 'void' | 'pay' | null;

/**
 * 🔒 The Invoice's page (piece 22; decision 0409): view mode draws Regions 1 to
 * 10 over the projection the server built, then the invoice's own lines and its
 * money beneath; edit mode is the form. The footer carries the status's one
 * primary action — Issue on a Draft, Clear the dispute on a disputed invoice —
 * with Mark as disputed, Write off and Void beside it.
 *
 * 🔨 A written-off invoice is never voided: the sale happened, and a payment
 * arriving afterwards reverses the write-off first (decision 0197).
 */
export function InvoicePage({ invoiceId, apiBaseUrl = '' }: { invoiceId: string; apiBaseUrl?: string }) {
  // 🔒 The banner's pills read the shell's one status resolver, as the lists do (decision 0639).
  const resolveStatus = useStatusResolver();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const creating = invoiceId === NEW_DOCUMENT_ID;
  /**
   * 🔨 The address asking for edit mode is not the answer. `mayEdit` is, and it
   * is the server's own, so the edit screen does not open on an issued invoice
   * even when `?mode=edit` is typed in by hand — matching the Edit control the
   * same flag already withholds (found in review, 2026-09-15). Before this the
   * form opened, populated, and only the save was refused.
   */
  const asked = documentPageMode(params.get('mode'), invoiceId) === 'edit' && !creating;
  // 🔒 The page's answer carries the invoice's History beside it (decisions 0470, 1221).
  const page = useApi<InvoiceAnswer & { history?: TileRow[] }>(creating ? null : `${INVOICES_PATH}/${invoiceId}`, apiBaseUrl);
  const zone = useViewerZone();
  const editing = creating || (asked && page.state.kind === 'ready' && page.state.data.footer?.mayEdit === true);
  /**
   * 🔒 Leaving edit mode re-reads the invoice (T925, the page keeping the old
   * lines after a draft's save; piece 22). The form saves and navigates back to
   * this same page, which stays mounted, so without the reload it drew the read
   * it held before the edit, the way the quote page's edit reloads on save.
   */
  const wasEditing = useRef(editing);
  const reloadPage = page.reload;
  useEffect(() => {
    if (wasEditing.current && !editing && !creating) reloadPage();
    wasEditing.current = editing;
  }, [editing, creating, reloadPage]);
  const [modal, setModal] = useState<OpenModal>(null);
  // 🔒 The write-off's own day, today in the viewer's zone to start with (decision 8860).
  const today = useDocumentToday();
  const [writeOffDate, setWriteOffDate] = useState(today);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The unpriced commission's warning, set aside for this visit only; it stands again on the next (decision 1221).
  const [unpricedDismissed, setUnpricedDismissed] = useState(false);

  // Save and Issue lands here with `act=issue`, so the issue opens on the saved invoice rather than on a form that has not written yet.
  const askedAct = params.get('act');
  // 🔒 The Checkout's return carries its page and its gateway back to the invoice it paid (decision 1650).
  const checkoutSession = params.get(PAY_RETURN_SESSION_PARAM);
  const checkoutGateway = params.get(PAY_RETURN_GATEWAY_PARAM);
  const [askDone, setAskDone] = useState(false);
  const open: OpenModal = modal ?? (askedAct === 'issue' && !editing && !askDone ? 'issue' : null);
  const close = () => {
    setModal(null);
    setAskDone(true);
  };

  /**
   * 🔨 `act` leaves the window open on a refusal on purpose. Dispute, Write off
   * and Void each hold their own reason and lose it on closing (decision 2870),
   * so a refused dispute cannot arrive as the reason a later write-off is recorded under.
   */
  const openModal = (which: Exclude<OpenModal, null>) => {
    // 🔒 The write-off window opens on today each time (decision 8860), never on a day typed into an earlier opening.
    if (which === 'writeOff') setWriteOffDate(today);
    setModal(which);
  };

  if (editing) return <InvoiceForm invoiceId={creating ? null : invoiceId} apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async <T,>(path: string, body: unknown, ok: string | ((data: T) => string)) => {
    setBusy(true);
    const answer = await post<T>(`${INVOICES_PATH}/${invoiceId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setNotice(`!${answer.message}`);
      return false;
    }
    close();
    setNotice(typeof ok === 'string' ? ok : ok(answer.data));
    page.reload();
    return true;
  };

  /** 🔒 Send issues a Draft as Issue does and mails the invoice's own PDF, and says who it went to (decision 2630). */
  const sendInvoice = (fields: DocumentSendFields, sendAnyway: boolean) =>
    void act<{ to: string; sent: string }>('send', { ...fields, ...(sendAnyway ? { sendAnyway: true } : {}) }, (data) =>
      data.sent === 'Accepted' || data.sent === 'Deferred' ? line(t('document.send.sentTo'), data.to) : t('document.send.failed'),
    );

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(invoice) => {
        const stale = staleSendFor('Invoice', invoice.staleSend, zone);
        const facts: ScreenFact[] = [
          // 🔒 The number line: the number once given, and the sentence saying when it will be while the invoice is a Draft (T682).
          { label: t('invoice.form.number'), value: invoice.invoiceNumber ?? t('invoice.form.numberOnIssue') },
          { label: t('invoice.fact.account'), value: invoice.companyName, route: `/companies/${invoice.companyId}` },
          { label: t('invoice.fact.salesOrder'), value: invoice.sourceSalesOrderNumber, route: invoice.sourceSalesOrderId ? `/sales-orders/${invoice.sourceSalesOrderId}` : null },
          // 🔒 The job this invoice billed, as a link (decision 0502); nothing is drawn where the invoice billed no job.
          { label: t('invoice.fact.workOrder'), value: invoice.workOrderNumber, route: invoice.workOrderId ? `/work-orders/${invoice.workOrderId}` : null },
          { label: t('invoice.fact.invoiceDate'), value: invoice.invoiceDate },
          { label: t('invoice.fact.dueDate'), value: invoice.dueDate },
          { label: t('invoice.fact.terms'), value: invoice.paymentTerms },
          { label: t('invoice.fact.paid'), value: <Money cents={invoice.amountPaidCents} currencyCode={invoice.currencyCode} /> },
          { label: t('invoice.fact.openBalance'), value: <Money cents={invoice.openBalanceCents} currencyCode={invoice.currencyCode} /> },
        ];
        // 🔒 Pay this invoice, the portal login's own, where the server offers it (decision 1650). Paying writes nothing on
        // the invoice, so it stands beside the write refusal every outside login reads rather than behind it.
        const payAction = invoice.payOnline ? (
          <span data-invoice-action="payOnline">
            <Button size={Size.Medium} color={Color.Navy} text={[t('invoice.pay.action')]} actions={[() => setModal('pay')]} disabled={busy} />
          </span>
        ) : null;
        const footer = (
          <>
            {/* 🔨 Edit is the action bar's primary and is drawn there alone (decision 0612). */}
            {/* 🔒 A Draft's Send carries Issue behind its chevron (decision 2630, amending 0436): Send issues and mails, Issue mails nothing. */}
            {invoice.footer?.primary === 'issue' ? (
              <Button size={Size.Medium} color={Color.Navy} text={[t('invoice.action.send'), t('invoice.action.issue')]} actions={[() => openModal('send'), () => openModal('issue')]} disabled={busy} />
            ) : null}
            {/* 🔒 An issued invoice takes Send again, which mails the issued copy with no second issue (decision 2630). */}
            {invoice.footer?.mayResend ? (
              <span data-invoice-action="resend">
                <Button size={Size.Medium} text={[t('document.send.again')]} actions={[() => openModal('send')]} disabled={busy} />
              </span>
            ) : null}
            {invoice.footer?.primary === 'clearDispute' ? (
              <Button size={Size.Medium} color={Color.Navy} text={[t('invoice.action.clearDispute')]} actions={[() => void act('clear-dispute', {}, t('invoice.disputeCleared'))]} disabled={busy} />
            ) : null}
            {/* 🔒 Record a payment from the invoice itself: it opens a new Payment on this invoice, never a second payment form of the Invoice's own (decision 0409; piece 22, the payment's screens). */}
            {invoice.footer?.mayRecordPayment ? (
              <span data-invoice-action="recordPayment">
                <Button size={Size.Medium} text={[t('invoice.action.recordPayment')]} actions={[() => navigate(`${LIST_PAGES.Payment.route}/${NEW_DOCUMENT_ID}?invoiceId=${encodeURIComponent(invoice.id)}`)]} disabled={busy} />
              </span>
            ) : null}
            {/* 🔒 Raise a credit note from the invoice it credits: the note's form opens with this invoice handed over (T921; decision 1704). */}
            {invoice.footer?.mayRaiseCreditNote ? (
              <span data-invoice-action="raiseCreditNote">
                <Button size={Size.Medium} text={[t('invoice.action.raiseCreditNote')]} actions={[() => navigate(`${LIST_PAGES.CreditNote.route}/${NEW_DOCUMENT_ID}?invoiceId=${encodeURIComponent(invoice.id)}`)]} disabled={busy} />
              </span>
            ) : null}
            {invoice.footer?.mayDispute ? <Button size={Size.Medium} text={[t('invoice.action.dispute')]} actions={[() => openModal('dispute')]} disabled={busy} /> : null}
            {invoice.footer?.mayWriteOff ? <Button size={Size.Medium} text={[t('invoice.action.writeOff')]} actions={[() => openModal('writeOff')]} disabled={busy} /> : null}
            {/* 🔒 A written-off invoice offers Undo Write-Off, asking for a reason first (decision 8860). */}
            {invoice.footer?.mayUndoWriteOff ? (
              <span data-invoice-action="undoWriteOff">
                <Button size={Size.Medium} text={[t('invoice.action.undoWriteOff')]} actions={[() => openModal('undoWriteOff')]} disabled={busy} />
              </span>
            ) : null}
            {invoice.footer?.mayVoid ? <Button size={Size.Medium} color={Color.Inactive} text={[t('invoice.action.void')]} actions={[() => openModal('void')]} disabled={busy} /> : null}
            {invoice.footer?.refusal ? <span className={styles.refusal} data-invoice-refusal="true">{invoice.footer.refusal}</span> : null}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
          </>
        );
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={invoice.currencyCode} />;
        /** 🔒 The lines as the one read-only table draws them for every kind (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('invoice.lines.title'),
          rowAttribute: 'data-invoice-line',
          blockAttribute: 'data-invoice-lines',
          rows: (invoice.lines ?? []).map((row) => ({
            id: row.id,
            label: row.sku ? line(row.sku, '·', row.description) : row.description,
            cells: [line(String(row.quantity), row.unitName), row.unitPriceCents === null ? '' : listPricedCell(moneyOf(row.unitPriceCents), row.listPriceCents, invoice.currencyCode)],
            amount: moneyOf(row.amountCents),
            taxable: row.taxable === true,
          })),
          totals: [
            // 🔒 One Tax line per jurisdiction the invoice stored, with its rate and its base (decision 0738); an older invoice draws its one.
            ...((invoice.taxRows ?? []).length > 0
              ? (invoice.taxRows ?? []).map((row) => ({ key: `tax-${row.level}-${row.label}`, label: <>{taxJurisdictionRowLead(row)} {moneyOf(row.taxableBaseCents)}</>, value: moneyOf(row.taxCents), attributes: { 'data-invoice-tax': String(row.taxCents), 'data-tax-level': row.level } }))
              : invoice.taxCents > 0 ? [{ label: t('invoice.lines.tax'), value: moneyOf(invoice.taxCents), attributes: { 'data-invoice-tax': String(invoice.taxCents) } }] : []),
            { label: t('invoice.lines.total'), value: moneyOf(invoice.totalCents), attributes: { 'data-invoice-total': String(invoice.totalCents) } },
          ],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(invoice);
        const notesPanel = <DocumentNotes recordType="Invoice" recordId={invoice.id} recordLabel={invoice.invoiceNumber ?? undefined} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // One Account Journal tile, placed in the Details view and handed to the PDF view's rail as its Journal tab (T1070; decision 1662).
        const journalPanel = <AccountJournalTile sourceDocumentType="Invoice" sourceDocumentId={invoice.id} apiBaseUrl={apiBaseUrl} />;
        // One Files block, placed in the Details view and handed to the PDF view's rail as its Files tab (T1070; decision 1662).
        const filesPanel = (
          <section className={styles.block} data-invoice-files={invoice.files.length}>
            <h2 className={styles.heading}>{t('documentFiles.title')}</h2>
            <DocumentFilesPanel recordType="Invoice" recordId={invoice.id} files={invoice.files} apiBaseUrl={apiBaseUrl} onChanged={page.reload} writeRefusal={invoice.writeRefusal ?? null} />
          </section>
        );
        // One History panel, placed in the Details view and handed to the PDF view's rail as its History tab (T1070; decision 1662).
        const historyPanel = <DocumentHistoryPanel recordType="Invoice" recordId={invoice.id} rows={invoice.history ?? []} timeZone={zone} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            journal={journalPanel}
            history={historyPanel}
            documentType="Invoice"
            icon="file"
            title={invoice.invoiceNumber ?? draftDocumentTitle('Invoice')}
            subtitle={pageKindLineText({ page: 'document', documentType: 'Invoice' })}
            information={facts}
            controls={<HelpButton topic="writeOffVsVoid" />}
            status={<BannerPills pills={invoicePills(invoice, resolveStatus)} attribute="data-invoice-pills" hint={invoice.approval?.reason ?? null} />}
            footer={footer}
            footerAttributes={{ 'data-invoice-actions': invoice.footer?.primary ?? 'none' }}
            pageAttributes={{ 'data-invoice': invoice.id, 'data-invoice-status': invoice.status, 'data-invoice-stored-status': invoice.storedStatus }}
            print={invoice.printDocument ?? null}
            layout={invoice.printLayout ?? null}
            sheet={invoice.printSheet ?? null}
            writeRefusal={invoice.writeRefusal ?? null}
            readerAction={payAction}
            ribbon={invoice.status}
            pdfPath={documentPdfPath(INVOICES_PATH, invoice.id)}
            listRoute={LIST_PAGES.Invoice.route}
            {...(invoice.footer?.mayEdit ? { onEdit: () => navigate(`${LIST_PAGES.Invoice.route}/${invoice.id}?mode=edit`) } : {})}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: invoice.id, onSaved: page.reload }}
            lines={lines}
            whatsNext={
              /* 🔒 The footer's own moves, named as the next step (decision 1732): a Draft's is Send, the footer's face, opening the same send window (decision 2691). */
              <WhatsNextBanner
                documentType="Invoice"
                status={invoice.status}
                disabled={busy}
                actions={{
                  ...(invoice.footer?.primary === 'issue' ? { send: () => openModal('send') } : {}),
                  ...(invoice.footer?.mayRecordPayment ? { recordPayment: () => navigate(`${LIST_PAGES.Payment.route}/${NEW_DOCUMENT_ID}?invoiceId=${encodeURIComponent(invoice.id)}`) } : {}),
                }}
              />
            }
            notices={
              <>
                {checkoutSession && checkoutGateway ? <PayReturnNotice invoiceId={invoice.id} session={checkoutSession} gateway={checkoutGateway} apiBaseUrl={apiBaseUrl} onPaid={page.reload} /> : null}
                {/* 🔒 An issue that left the commission unpriced says so in the warning below, and the plain success line is not drawn beside it (decision 1221). */}
                {notice && !(notice === t('invoice.issued') && invoice.unpricedCommission) ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {/*
                  🔒 The commission waits for a rate (decision 1221): the warning's sentence and what this viewer can do about it.
                  Open Commission Rate Table is offered only where the server named the route; anyone else dismisses it.
                */}
                {invoice.unpricedCommission && !unpricedDismissed ? (
                  <div data-invoice-unpriced-commission="true">
                    <AlertBanner
                      tone="warning"
                      message={`${invoice.unpricedCommission.message} ${invoice.unpricedCommission.guidance}`}
                      action={
                        invoice.unpricedCommission.rateTableRoute
                          ? { label: t('commission.unpriced.openRateTable'), onAction: () => navigate(invoice.unpricedCommission!.rateTableRoute!) }
                          : { label: t('modal.dismiss'), onAction: () => setUnpricedDismissed(true) }
                      }
                      {...(invoice.unpricedCommission.rateTableRoute ? { onDismiss: () => setUnpricedDismissed(true) } : {})}
                    />
                  </div>
                ) : null}
                {/* 🔒 Decision 2594 (T1175): past the customer PO's amount the invoice warns, and Issue still goes through. */}
                {invoice.customerPoWarning ? <div data-invoice-po-warning="true"><AlertBanner tone="warning" urging message={invoice.customerPoWarning} /></div> : null}
                {/* 🔒 The approval block: the order's approval in the order's own words, or no rule where no order stands behind the invoice (T131; decision 0713). */}
                <ApprovalReason view={invoice.approval} />
                {invoice.disputeReason ? <p className={styles.rejection} data-invoice-dispute>{line(t('invoice.action.dispute'), '·', invoice.disputeReason)}</p> : null}
                {invoice.writeOffReason ? <p className={styles.sentence} data-invoice-write-off>{line(t('invoice.action.writeOff'), '·', invoice.writeOffReason)}</p> : null}
              </>
            }
            details={
              <>
                {/* 🔒 What this document posted and where each amount was placed (decision 1075). */}
                {journalPanel}
                {/* 🔒 The Deal's team as it stood when this document was created (decision 0765). */}
                <DealTeamPanel team={invoice.dealTeam} timeZone={zone} />
                {/* 🔒 Where the newest mailed send stands, one line per recipient (decisions 1226, 2630); nothing is drawn where nothing was mailed. */}
                <DeliveredPanel delivery={invoice.delivery ?? null} timeZone={zone} />
                {/* 🔒 The files attached to this invoice — the customer's purchase order among them (decision 0457). */}
                {filesPanel}

                {/* 🔒 The order this invoice is for, named after the fact and only while it is a Draft (decision 0469). */}
                <SourceLinkPanel
                  path={INVOICES_PATH}
                  recordId={invoice.id}
                  sourceKind="SalesOrder"
                  sourceLabel={invoice.sourceSalesOrderNumber}
                  sourceRoute={invoice.sourceSalesOrderId ? `/sales-orders/${invoice.sourceSalesOrderId}` : null}
                  editable={invoice.status === 'Draft'}
                  apiBaseUrl={apiBaseUrl}
                  onChanged={page.reload}
                />
                {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
                {notesPanel}
                {/* 🔒 What has happened to the invoice, the commission's unpriced and priced entries among it (decisions 0470, 1221). */}
                {historyPanel}
              </>
            }
          >

              {open === 'pay' && invoice.payOnline ? <PayInvoiceModal invoiceId={invoice.id} offer={invoice.payOnline} onCancel={close} apiBaseUrl={apiBaseUrl} /> : null}
              {open === 'send' ? (
                /* 🔒 The send window, the Quote's over this invoice: To, Message, the stale warning on a first send and the last send (decision 2630). */
                <DocumentSendWindow
                  title={t('invoice.mail.title')}
                  dataModal="invoice-mail"
                  sendLabel={t('invoice.action.send')}
                  hints={[invoice.storedStatus === 'Draft' ? t('invoice.mail.issueHint') : t('invoice.mail.resendHint'), t('invoice.mail.toHint')]}
                  stale={invoice.storedStatus === 'Draft' ? stale : null}
                  previousDelivery={invoice.delivery ?? null}
                  timeZone={zone}
                  busy={busy}
                  onSend={sendInvoice}
                  onCancel={close}
                  mailbox={invoice.sendMailbox ?? null}
                  onChangeDate={() => {
                    close();
                    navigate(`${LIST_PAGES.Invoice.route}/${invoice.id}?mode=edit`);
                  }}
                />
              ) : null}
              {open === 'issue' ? (
                <Modal
                  open
                  title={t('invoice.issue.title')}
                  onCancel={close}
                  dataModal="invoice-issue"
                  actions={[
                    { kind: 'primary', label: stale ? t('document.staleSend.issueAnyway') : t('invoice.action.issue'), onAct: () => void act('issue', stale ? { sendAnyway: true } : {}, t('invoice.issued')), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  {/* 🔒 A first issue of an old-dated invoice warns, and Issue Anyway is what the audit row records (decision 1227). */}
                  <StaleSendBanner
                    warning={stale}
                    onChangeDate={() => {
                      close();
                      navigate(`${LIST_PAGES.Invoice.route}/${invoice.id}?mode=edit`);
                    }}
                  />
                  <p className={modalStyles.fieldLabel}>{t('invoice.issue.hint')}</p>
                </Modal>
              ) : null}
              {open === 'dispute' ? (
                <ReasonWindow
                  open
                  title={t('invoice.dispute.title')}
                  label={t('invoice.dispute.reason')}
                  actLabel={t('invoice.action.dispute')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="invoice-dispute"
                  onAct={(reason) => act('dispute', { reason }, t('invoice.disputed'))}
                />
              ) : null}
              {open === 'writeOff' ? (
                <ReasonWindow
                  open
                  title={t('invoice.writeOff.title')}
                  label={t('invoice.writeOff.reason')}
                  actLabel={t('invoice.action.writeOff')}
                  hint={t('invoice.writeOff.hint')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="invoice-write-off"
                  ready={writeOffDate !== ''}
                  notReadyReason={t('invoice.writeOff.dateNeeded')}
                  onAct={(reason) => act('write-off', { reason, onDate: writeOffDate }, t('invoice.writtenOff'))}
                >
                  {/* 🔒 The write-off's own day, today preselected; the entry is dated that day (decision 8860). */}
                  <WriteOffDateField value={writeOffDate} onChange={setWriteOffDate} />
                  {/* 🔒 The help stands in the window where Write Off is chosen, not behind it on the banner (decisions 0443, 3063). */}
                  <span data-window-help="writeOffVsVoid"><HelpButton topic="writeOffVsVoid" /></span>
                </ReasonWindow>
              ) : null}
              {open === 'undoWriteOff' ? (
                <ReasonWindow
                  open
                  title={t('invoice.undoWriteOff.title')}
                  label={t('invoice.undoWriteOff.reason')}
                  actLabel={t('invoice.action.undoWriteOff')}
                  hint={t('invoice.undoWriteOff.hint')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="invoice-undo-write-off"
                  onAct={(reason) => act('undo-write-off', { reason }, t('invoice.writeOffUndone'))}
                />
              ) : null}
              {open === 'void' ? (
                <ReasonWindow
                  open
                  title={t('invoice.void.title')}
                  label={t('invoice.void.reason')}
                  actLabel={t('invoice.action.void')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="invoice-void"
                  onAct={(reason) => act('void', { reason }, t('invoice.voided'))}
                >
                  {/* 🔒 The help stands in the window where Void is chosen, not behind it on the banner (decisions 0443, 3063). */}
                  <span data-window-help="writeOffVsVoid"><HelpButton topic="writeOffVsVoid" /></span>
                </ReasonWindow>
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}
